const messageTypes = {
	"lti.capabilities": { returns: true, matchOrigin: false },
	"lti.put_data": { returns: true, matchOrigin: true },
	"lti.get_data": { returns: true, matchOrigin: true },
} as const;

export async function lmsAuthPostMessageListen(
	timeout: number,
): Promise<{ success: true } | { success: false; error?: string }> {
	return new Promise((resolve) => {
		let handlerRemoved = false;
		const lmsAuthPostMessageHandler = (event: MessageEvent) => {
			if (
				event.origin !== location.origin ||
				typeof event.data !== "object" ||
				event.data.subject !== "__VERITY_LMS_AUTH"
			) {
				return;
			}
			if (event.data.success === true) {
				resolve({ success: true });
			} else if (event.data.success === false) {
				resolve({ success: false, error: event.data.error });
			}
			handlerRemoved = true;
			window.removeEventListener("message", lmsAuthPostMessageHandler); // cleanup
			resolve({ success: false });
		};
		window.addEventListener("message", lmsAuthPostMessageHandler);
		// cleanup if no timely message
		setTimeout(() => {
			if (!handlerRemoved) {
				window.removeEventListener("message", lmsAuthPostMessageHandler);
				resolve({ success: false, error: "Authorization timed out." });
			}
		}, timeout);
	});
}

export async function lmsPostMessage<T extends keyof typeof messageTypes>(
	window: Window,
	origin: string,
	subject: T,
	properties: unknown,
	frameName?: string,
): Promise<{ response?: unknown }> {
	const parent = (window.opener as Window | null) || window.parent;

	const targetFrames =
		!frameName || frameName === "_parent" ? [parent] : [parent.frames[frameName as unknown as number]];

	const messageId = crypto.randomUUID();
	const parentMessageId = crypto.randomUUID();
	const timeout = 250;

	return new Promise((resolve, reject) => {
		if (targetFrames.length === 0) {
			return reject("No frames found.");
		}
		if (typeof properties !== "object") {
			return reject("Properties is not an object.");
		}

		const controller = new AbortController();
		window.addEventListener(
			"message",
			(event) => {
				// unexpected message response
				if (typeof event.data !== "object") {
					return;
				}
				// incorrect response type
				if (event.data.subject !== subject + ".response") {
					return;
				}
				// message id does not match
				if (event.data.message_id !== messageId && event.data.message_id !== parentMessageId) {
					return;
				}
				controller.abort();
				if (event.data.error) {
					return reject(event.data.error.message);
				}
				if (messageTypes[subject].matchOrigin && event.data.message_id === messageId && event.origin !== origin) {
					return reject(`Response origin does not match. ${event.origin} - ${origin}`);
				}
				return resolve({ response: event.data });
			},
			{
				signal: controller.signal,
			},
		);

		for (const frame of targetFrames) {
			frame.postMessage(
				{
					subject: subject,
					message_id: messageId,
					...properties,
				},
				origin,
			);
		}

		// no return expected, resolve
		if (!messageTypes[subject].returns) {
			controller.abort();
			return resolve({});
		}

		// fallback postMessage to parent window with wildcard * origin when
		// sending to specified frame with platform origin fails to give timely response
		setTimeout(() => {
			if (!controller.signal.aborted) {
				parent.postMessage(
					{
						subject: subject,
						message_id: parentMessageId,
						...properties,
					},
					"*",
				);
				setTimeout(() => {
					if (!controller.signal.aborted) {
						controller.abort();
						return reject("postMessage timed out.");
					}
				}, timeout);
			}
		}, timeout);
	});
}

export async function lmsCheckCapabilityThenPostMessage<
	T extends Exclude<keyof typeof messageTypes, "lti.capabilities">,
>(window: Window, subject: T, properties: unknown): Promise<{ response?: unknown } | null> {
	const capabilities = (await lmsPostMessage(window, "*", "lti.capabilities", {})) as {
		response?: { supported_messages?: { subject: string; frame?: string }[] };
	};
	if (
		capabilities.response &&
		typeof capabilities.response === "object" &&
		Object.hasOwn(capabilities.response, "supported_messages") &&
		capabilities.response["supported_messages"] instanceof Array
	) {
		const frameName = capabilities.response["supported_messages"].find((message) => message.subject === subject)?.frame;
		const origin = new URL(window !== window.parent ? window.location.ancestorOrigins[0] : document.URL).origin;
		return await lmsPostMessage(window, origin, subject, properties, frameName);
	}
	return null;
}

export function hasKeyAndValue(item: unknown): item is { key: unknown; value: unknown } {
	return typeof item === "object" && item != null && Object.hasOwn(item, "key") && Object.hasOwn(item, "value");
}
